import { View, type ViewData, mustBeHTMLElementOfType } from "silver";
import { type AuthLang } from "Lang";
import { type MyonEnv } from "shared/Api/MyonEnv";
import { AutoComplete, type AutoCompleteRow } from "core/Controls/AutoComplete";
import { type LoginBuildingSearchResult } from "core/Services/LoginBuildingSearchService";
import { Misc } from "core/Misc";
import { $getLoginBuildingSearchService } from "core/services";

interface LoginView extends View<ViewData> {
	buildingId: HTMLInputElement;
	buildingMessage: HTMLDivElement;
	buildingText: HTMLInputElement;
	authMessage: HTMLDivElement;
	usernameInput: HTMLInputElement;
	passwordInput: HTMLInputElement;
	submitInput: HTMLInputElement;
}

type LoginAutoCompleteRow = AutoCompleteRow & {
	apiResult: LoginBuildingSearchResult;
};

export default class Login {
	private view: LoginView;
	private autoComplete: AutoComplete<LoginAutoCompleteRow>;

	constructor(
		env: MyonEnv,
		buildingMessage: string|null,
		authMessage: string|null,
		authLang: AuthLang,
	) {

		const signinElm = mustBeHTMLElementOfType(document.getElementById("signin"), "form");

		this.view = new View<ViewData>(signinElm, { }, { }) as LoginView;

		this.autoComplete = new AutoComplete<LoginAutoCompleteRow>(
			this.view.buildingText,
			async (query: string) => {
				const results = await $getLoginBuildingSearchService(env).search(query);
				return results.map(row => ({
					apiResult: row,
					value:     row.building,
					label:     `${row.building}, ${row.account}`,
					html:      (queryText: string): string => {
						const quotedQueryText = Misc.regexpEscape(queryText || "");
						const reg = new RegExp(quotedQueryText, "i");
						const building = row.building.replace(reg, (match: string) => `<em class="autoComplete_searchHighlight">${match}</em>`);

						let ext = "";
						let title = "";
						if(row.host && row.host !== env.login.buildingSearch.canonicalHost) {
							ext = ` <span style="font-size: .8em">✈</span>`;
							title = `Logs in to ${row.host}`;
						}

						return `<div class="flex -row -gutter" title="${title}">
							<span class="flex_item -grow">
								${building}
							</span>
							<span class="flex_item autoComplete_detail">${row.account}${ext}</span>
						</div>`;
					}
				}));
			}
		);
		this.autoComplete.requiredCharacterCount = 2;
		this.autoComplete.autoSelect = true;
		this.autoComplete.labelledBy = "buildingTextLabel";
		this.autoComplete.onDeselect(() => {
			this.view.buildingId.value = "";
			this.view.buildingText.classList.remove("-valid");
		});
		this.autoComplete.onSelect((event) => {
			this.view.buildingId.value = String(event.row.apiResult.id);
			this.view.buildingText.classList.add("-valid");
			this.setBuildingMessage(event.row.apiResult.message || "");
			this.toggleLoginForm(event.row.apiResult.allowLogin);

			if(event.row.apiResult.host) {
				const target = new URL(window.location.toString());

				// If we match the canonical host, we want to stay on the current host.
				// For example, if we're on standby.myon.com and the user selects
				// a building on www.myon.com, we want to stay on standby.myon.com
				if (event.row.apiResult.host !== env.login.buildingSearch.canonicalHost) {
					target.host = event.row.apiResult.host;
				}

				signinElm.action = target.toString();
			}
		});
		this.autoComplete.resultCountLabeler = authLang.N_RESULTS_FOUND;
		this.autoComplete.instructions = authLang.AUTOCOMPLETE_INSTRUCTIONS();

		if (buildingMessage) {
			this.setBuildingMessage(buildingMessage);
		}
		if (authMessage) {
			this.view.authMessage.innerHTML = authMessage; // see PR 11000
			this.view.authMessage.classList.remove("-hidden");
			this.view.usernameInput.setAttribute("aria-description", authMessage);
		}
		this.view.usernameInput.focus();
	}

	private setBuildingMessage(message: string) {
		this.view.buildingMessage.innerHTML = message;
		this.view.buildingMessage.classList.toggle("-hidden", !message);
	}

	public toggleLoginForm(toggle: boolean) {
		this.view.usernameInput.disabled = !toggle;
		this.view.passwordInput.disabled = !toggle;
		this.view.submitInput.disabled = !toggle;
		this.view.submitInput.classList.toggle("-disabled", !toggle);
	}
}
